/**
 * Renders a DQDL rule expression with collapsible overflow. Short rules display inline; long rules
 * are clamped to 2 lines with a toggle to expand.
 */

import Button from "@cloudscape-design/components/button"
import SpaceBetween from "@cloudscape-design/components/space-between"
import { type CSSProperties, type ReactNode, useLayoutEffect, useRef, useState } from "react"

// Inline-code styling applied to the same element that is clamped and measured. Keeping the clamp,
// padding, and measured node on one element (rather than a clamp wrapper around a separate padded
// Box) is what makes scrollHeight/clientHeight comparable - otherwise short rules that fit report
// a false overflow because the wrapper's line metrics differ from the padded code content.
const CODE_STYLE: CSSProperties = {
  display: "block",
  fontFamily: '"Monaco", "Menlo", monospace',
  fontSize: "0.75rem",
  background: "rgba(0, 0, 0, 0.05)",
  padding: "0.125rem 0.375rem",
  borderRadius: "0.25rem",
  wordBreak: "break-word",
}

// Cloudscape has no line-clamp, so 2-line truncation stays a manual style on the code element.
const CLAMP_STYLE: CSSProperties = {
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
}

interface CollapsibleRuleProps {
  /**
   * The raw DQDL rule string. Mutually exclusive with children.
   */
  rule?: string
  /**
   * Rich JSX content to render inside the code block. Use when the rule needs inline formatting
   * (e.g. highlighted clauses). Mutually exclusive with rule.
   */
  children?: ReactNode
}

/**
 * Displays a DQDL rule in a code block. If the rendered text overflows 2 lines, a "Show more"
 * toggle appears to expand it. Rules that fit render without any affordance.
 */
export default function CollapsibleRule({ rule, children }: CollapsibleRuleProps) {
  const [expanded, setExpanded] = useState(false)
  const [isOverflowing, setIsOverflowing] = useState(false)
  const codeRef = useRef<HTMLElement>(null)

  useLayoutEffect(() => {
    const el = codeRef.current
    if (!el) return
    // Re-measure on resize so overflow is detected once the element becomes visible (e.g. when a
    // collapsed ExpandableSection is expanded, going from height 0 to its real size).
    const measure = () => setIsOverflowing(el.scrollHeight > el.clientHeight)
    measure()
    if (typeof ResizeObserver === "undefined") return
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [rule, children])

  const isCollapsible = isOverflowing || expanded

  return (
    <SpaceBetween size="xs">
      <code ref={codeRef} style={expanded ? CODE_STYLE : { ...CODE_STYLE, ...CLAMP_STYLE }}>
        {children ?? rule}
      </code>
      {isCollapsible && (
        // Stop the click from bubbling to any parent row/expand toggle.
        <span onClick={(e) => e.stopPropagation()}>
          <Button
            iconAlign="right"
            iconName={expanded ? "caret-up-filled" : "caret-down-filled"}
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? "Show less" : "Show more"}
          </Button>
        </span>
      )}
    </SpaceBetween>
  )
}
